import React, { useEffect, useRef } from 'react';

import { translate } from 'components/translate';
import ClubDate, { setDaysLabels } from 'helpers/ClubDate';
import DateHelper from 'helpers/Date';
import useDatePickerClick from 'hooks/useDatePickerClick';
import useInputChange from 'hooks/useInputChange';
import { inject } from 'mobx-react';
import PropTypes from 'prop-types';

import '@hotmart/cosmos/dist/date-picker';

const DatePicker = ({
  dateFormat = DateHelper.getShortFormatDate(),
  placeholder = '',
  relativeDate = '7d',
  date,
  disabledDates = [],
  'data-test': dataTest,
  clickInput = () => null,
  clickCancelButton = () => null,
  clickSaveButton = () => null,
  clickMonthButton = () => null,
  clickYearButton = () => null,
  onChange = () => null,
  onOpen = () => null,
  onClose = () => null,
  locale,
  t
}) => {
  const inputDatePickerRangeRef = useRef(null);

  useInputChange(inputDatePickerRangeRef, onChange);
  useDatePickerClick(inputDatePickerRangeRef, onOpen, onClose);

  useEffect(() => {
    const hotDatePicker = inputDatePickerRangeRef?.current;
    const datePickerInput = hotDatePicker?.shadowRoot?.getElementById(
      'date-picker-input-fake'
    );
    const hotDatePickerConcrete = document.querySelector(
      'hot-date-picker-concrete'
    );

    const buttonCancel =
      hotDatePickerConcrete?.shadowRoot?.getElementById('button-cancel');
    const buttonSave =
      hotDatePickerConcrete?.shadowRoot?.getElementById('button-apply');
    const monthSelect = hotDatePickerConcrete?.shadowRoot?.querySelector(
      '.flatpickr-monthDropdown-months'
    );
    const yearSelect =
      hotDatePickerConcrete?.shadowRoot?.querySelector('.numInputWrapper');

    datePickerInput?.addEventListener('click', clickInput);
    buttonCancel?.addEventListener('click', clickCancelButton);
    buttonSave?.addEventListener('click', clickSaveButton);
    monthSelect?.addEventListener('click', clickMonthButton);
    yearSelect?.addEventListener('click', clickYearButton);

    return () => {
      datePickerInput?.removeEventListener('click', clickInput);
      buttonCancel?.removeEventListener('click', clickCancelButton);
      buttonSave?.removeEventListener('click', clickSaveButton);
      monthSelect?.removeEventListener('click', clickMonthButton);
      yearSelect?.removeEventListener('click', clickYearButton);
    };
  }, [
    inputDatePickerRangeRef,
    clickInput,
    clickCancelButton,
    clickSaveButton,
    clickMonthButton,
    clickYearButton
  ]);

  useEffect(
    function setLocaleLabels() {
      const daysLabels = {};

      Object.assign(daysLabels, setDaysLabels);
      Object.keys(setDaysLabels).forEach((daysLabel) => {
        if (placeholder && daysLabel === 'start-date-placeholder') {
          daysLabels[daysLabel] = t(placeholder);
        } else {
          daysLabels[daysLabel] = t(daysLabels[daysLabel]);
        }
      });

      inputDatePickerRangeRef?.current?.setLocale(
        ClubDate.CosmosLocale,
        daysLabels
      );
    },
    [locale, t, placeholder]
  );

  useEffect(() => {
    inputDatePickerRangeRef?.current?.setDisabledDates(disabledDates);
  }, [disabledDates]);

  useEffect(() => {
    const hotDatePicker = inputDatePickerRangeRef?.current;
    const datePickerInput = hotDatePicker?.shadowRoot?.querySelector(
      '#date-picker-input-fake'
    );

    if (datePickerInput) {
      datePickerInput.placeholder = placeholder
        ? t(placeholder)
        : t('date_picker.end_date_placeholder');
    }
  }, [placeholder, t]);

  return (
    <hot-date-picker
      ref={inputDatePickerRangeRef}
      date-format={dateFormat}
      relative-date={relativeDate}
      data-test={dataTest}
      date={date}
    />
  );
};

DatePicker.propTypes = {
  'dateFormat': PropTypes.string,
  'locale': PropTypes.string,
  'placeholder': PropTypes.string,
  'relativeDate': PropTypes.string,
  'date': PropTypes.string,
  'disabledDates': PropTypes.array,
  'data-test': PropTypes.string,
  'clickInput': PropTypes.func,
  'clickCancelButton': PropTypes.func,
  'clickSaveButton': PropTypes.func,
  'clickMonthButton': PropTypes.func,
  'clickYearButton': PropTypes.func,
  'onChange': PropTypes.func,
  'onOpen': PropTypes.func,
  'onClose': PropTypes.func,
  't': PropTypes.func
};

export default inject((stores) => ({
  locale: stores.UserStore.locale
}))(translate()(DatePicker));
